import React from 'react';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Slider from '@material-ui/core/Slider';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { clampVideoDuration, getOptionsForModel } from './helpers';

const useStyles = makeStyles((theme) => ({
  section: {
    marginTop: theme.spacing(2),
  },
  modelSelect: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minWidth: 220,
  },
  length: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    maxWidth: 560,
  },
  lengthLabel: {
    flexShrink: 0,
    marginRight: theme.spacing(2),
  },
  lengthSlider: {
    flex: 1,
    minWidth: 0,
    marginRight: theme.spacing(2),
  },
  lengthInput: {
    width: 96,
    flexShrink: 0,
  },
}));

function OptionSelect({ id, label, value, options, onChange, disabled, className }) {
  return (
    <FormControl variant="outlined" fullWidth className={className}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select labelId={`${id}-label`} label={label} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
        {options.map((option) => (
          <MenuItem key={option.value || option.id} value={option.value || option.id}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default function MediaSettingsFields({
  imageModels,
  imageModel,
  imageResolution,
  imageAspectRatio,
  onImageModelChange,
  onImageResolutionChange,
  onImageAspectRatioChange,
  videoModels,
  videoModel,
  videoResolution,
  videoAspectRatio,
  videoDuration,
  onVideoModelChange,
  onVideoResolutionChange,
  onVideoAspectRatioChange,
  onVideoDurationChange,
  disabled,
}) {
  const classes = useStyles();
  const imageOptions = getOptionsForModel(imageModel);
  const videoOptions = getOptionsForModel(videoModel);

  return (
    <>
      <Typography variant="subtitle2">Image settings</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" fullWidth className={classes.modelSelect}>
            <InputLabel id="ai-image-model-label">Model</InputLabel>
            <Select
              labelId="ai-image-model-label"
              label="Model"
              value={imageModel}
              onChange={(e) => onImageModelChange(e.target.value)}
              disabled={disabled}
            >
              {imageModels.map((model) => (
                <MenuItem key={model.id} value={model.id}>
                  {model.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <OptionSelect
            id="ai-image-resolution"
            className={classes.modelSelect}
            label={imageOptions.resolutionLabel}
            value={imageResolution}
            options={imageOptions.resolutions}
            onChange={onImageResolutionChange}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <OptionSelect
            id="ai-image-aspect"
            className={classes.modelSelect}
            label={imageOptions.aspectRatioLabel}
            value={imageAspectRatio}
            options={imageOptions.aspectRatios}
            onChange={onImageAspectRatioChange}
            disabled={disabled}
          />
        </Grid>
      </Grid>

      <Typography className={classes.section} variant="subtitle2">
        Video settings
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" fullWidth className={classes.modelSelect}>
            <InputLabel id="ai-video-model-label">Model</InputLabel>
            <Select
              labelId="ai-video-model-label"
              label="Model"
              value={videoModel}
              onChange={(e) => onVideoModelChange(e.target.value)}
              disabled={disabled}
            >
              {videoModels.map((model) => (
                <MenuItem key={model.id} value={model.id}>
                  {model.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <OptionSelect
            id="ai-video-resolution"
            className={classes.modelSelect}
            label={videoOptions.resolutionLabel}
            value={videoResolution}
            options={videoOptions.resolutions}
            onChange={onVideoResolutionChange}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <OptionSelect
            id="ai-video-aspect"
            className={classes.modelSelect}
            label={videoOptions.aspectRatioLabel}
            value={videoAspectRatio}
            options={videoOptions.aspectRatios}
            onChange={onVideoAspectRatioChange}
            disabled={disabled}
          />
        </Grid>
      </Grid>
      <Box className={classes.length}>
        <Typography className={classes.lengthLabel} variant="body2" color="textSecondary">
          Video length
        </Typography>
        <Box className={classes.lengthSlider}>
          <Slider
            value={videoDuration}
            min={1}
            max={30}
            step={1}
            valueLabelDisplay="auto"
            onChange={(_, value) => onVideoDurationChange(clampVideoDuration(value))}
            aria-label="video length in seconds"
            disabled={disabled}
          />
        </Box>
        <TextField
          className={classes.lengthInput}
          type="number"
          size="small"
          variant="outlined"
          label="Seconds"
          value={videoDuration}
          inputProps={{ min: 1, max: 30, step: 1 }}
          onChange={(e) => onVideoDurationChange(clampVideoDuration(e.target.value))}
          disabled={disabled}
        />
      </Box>
    </>
  );
}
